import { LangText } from '@/components';
import { handleClick } from '@/components/OutLink';
import { JAPAN_MALL_URL } from '@/constants';
import { getLangExtField } from '@/constants/appLocale';
import {
  apiHanedaClassifications,
  apiHanedaGoodsPage,
  type HanedaClassification,
  type HanedaGoods,
} from '@/service/api';
import { PlatformEnum } from '@/service/api/types';
import { preFixImgUrl } from '@/utils';
import { Spin } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import emptyGoodsImg from '@/assets/svg/empty-goods.svg';

const PRODUCT_FRAME_IMG = preFixImgUrl('c22d471d57793b92e1ba8bcdb2728e1c.png');

const formatYen = (value: number) => `¥${value.toLocaleString()}`;

type ProductCardData = {
  id: number;
  name: string;
  langExt?: string;
  price: number;
  originalPrice: number;
  image: string;
};

const getClassificationName = (classification: HanedaClassification, language: string) =>
  getLangExtField(classification.langExt, language, 'name', classification.name);

const mapGoodsToProduct = (goods: HanedaGoods): ProductCardData => ({
  id: goods.spuId,
  name: goods.name,
  langExt: goods.langExt,
  price: goods.price ?? 0,
  originalPrice: goods.marketPrice ?? goods.price ?? 0,
  image: goods.fullImgUrl || goods.imgUrl || emptyGoodsImg,
});

const SectionTitle = () => {
  return (
    <div className="flex flex-col items-center">
      <LangText lngKey="home.haneda.title" className="text-[36px] font-bold leading-[50px] text-[#000]" />
      <span className="mt-[16px] h-[4px] w-[108px] rounded-full bg-primary" />
    </div>
  );
};

const ProductCard = ({ product }: { product: ProductCardData }) => {
  const { i18n } = useTranslation();
  const saving = Math.max(0, product.originalPrice - product.price);
  const name = getLangExtField(product.langExt, i18n.language, 'name', product.name);

  return (
    <div className="group overflow-hidden rounded-[20px] border border-[#D8D8D8] bg-white">
      <div className="relative h-[288px] overflow-hidden">
        <img
          src={product.image}
          alt=""
          className="object-cover absolute inset-0 w-full h-full transition-transform duration-500 ease-out group-hover:scale-110"
          draggable={false}
        />
        <img
          src={PRODUCT_FRAME_IMG}
          alt=""
          className="object-cover absolute inset-0 z-10 w-full h-full pointer-events-none"
          draggable={false}
        />
      </div>
      <div className="px-[12px] pb-[13px] pt-[20px]">
        <span className="line-clamp-2 min-h-[60px] text-[20px] font-bold leading-[30px] text-[#000]">
          {name}
        </span>
        <div className="flex items-end gap-[8px] mt-[12px]">
          <span className="text-[18px] font-bold leading-[27px] text-[#FB5C00]">
            {formatYen(product.price)}
          </span>
        </div>
      </div>
    </div>
  );
};

const ProductEmpty = () => {
  return (
    <div className="mt-[24px] flex h-[412px] flex-col items-center justify-center bg-white rounded-[12px]">
      <img src={emptyGoodsImg} alt="" className="h-[182px] w-[182px]" draggable={false} />
      <LangText lngKey="home.haneda.empty" className="mt-[38px] text-[20px] font-bold leading-[28px] text-[#000]" />
    </div>
  );
};

const ProductLoading = () => (
  <div className="mt-[24px] flex h-[412px] items-center justify-center rounded-[12px] bg-white">
    <Spin size="large" />
  </div>
);

export const HanedaProductFlow = () => {
  const { i18n } = useTranslation();
  const [classifications, setClassifications] = useState<HanedaClassification[]>([]);
  const [activeClassificationId, setActiveClassificationId] = useState<number | null>(null);
  const [products, setProducts] = useState<ProductCardData[]>([]);
  const [classificationLoading, setClassificationLoading] = useState(true);
  const [productLoading, setProductLoading] = useState(false);

  useEffect(() => {
    let ignore = false;

    const loadClassifications = async () => {
      setClassificationLoading(true);
      try {
        const response = await apiHanedaClassifications({
          onlyFirstLevel: true,
          storeId: null,
        });
        if (ignore) return;

        const nextClassifications = [...(response.data ?? [])].sort(
          (left, right) => (left.sort ?? 0) - (right.sort ?? 0),
        );
        setClassifications(nextClassifications);
        setActiveClassificationId(nextClassifications[0]?.id ?? null);
      } catch {
        if (ignore) return;
        setClassifications([]);
        setActiveClassificationId(null);
        setProducts([]);
      } finally {
        if (!ignore) setClassificationLoading(false);
      }
    };

    void loadClassifications();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (activeClassificationId === null) {
      setProducts([]);
      return;
    }

    let ignore = false;

    const loadProducts = async () => {
      setProductLoading(true);
      setProducts([]);
      try {
        const response = await apiHanedaGoodsPage({
          classificationId: activeClassificationId,
          current: 1,
          size: 4,
          keyword: '',
          storeId: null,
        });
        if (ignore) return;
        setProducts((response.data?.records ?? []).map(mapGoodsToProduct));
      } catch {
        if (!ignore) setProducts([]);
      } finally {
        if (!ignore) setProductLoading(false);
      }
    };

    void loadProducts();

    return () => {
      ignore = true;
    };
  }, [activeClassificationId]);

  const viewAllProducts = () => {
    void handleClick({
      platform: PlatformEnum.JAPANMALL,
      linUrl: JAPAN_MALL_URL,
    });
  };

  return (
    <section className="mx-auto w-[1200px] pt-[98px]">
      <SectionTitle />
      <div className="mt-[82px] flex items-center justify-between">
        <div className="flex items-center gap-[12px]">
          {(classifications || []).map((classification) => {
            const isActive = activeClassificationId === classification.id;
            return (
              <button
                key={classification.id}
                type="button"
                onClick={() => setActiveClassificationId(classification.id)}
                className={`h-[40px] min-w-[100px] rounded-[8px] px-[14px] text-[16px] font-medium transition-colors ${
                  isActive
                    ? 'border border-primary bg-secondary text-primary'
                    : 'border border-transparent bg-[#F2F2F2] text-[#979797]'
                }`}
              >
                {getClassificationName(classification, i18n.language)}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={viewAllProducts}
          className="h-[40px] rounded-full bg-primary px-[24px] text-[16px] font-medium text-white transition-opacity hover:opacity-90"
        >
          <LangText lngKey="common.action.viewAll" />
        </button>
      </div>
      {classificationLoading || productLoading ? (
        <ProductLoading />
      ) : products.length > 0 ? (
        <div className="mt-[24px] grid grid-cols-4 gap-[16px]">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      ) : (
        <ProductEmpty />
      )}
    </section>
  );
};
